import * as React from 'react';
import * as yup from 'yup';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  checkIfPhoneNumberIsValid,
  country,
  handleError,
  is500,
  muviPhoneNumberTrimmer,
} from '@/logic/shared';
import { privateBookingModel, useCreatePrivateBooking } from '@/logic/services';
import { useAuth, useCities, useGuest, useSnack } from '@/logic/state';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DatePicker } from '@/components/shared/localized-date-pickers';
import { DateTime } from 'luxon';
import InputAdornmentComponent from '@/components/shared/Input-adornment-component';
import ReCAPTCHA from 'react-google-recaptcha';
import RequiredGrayIcon from '@/components/shared/required-gray-icon';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { TimePicker } from '@/components/shared/localized-date-pickers';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { _DurationUnits } from '@/hooks/use-notifications';
import { MuiTelInput, MuiTelInputCountry, MuiTelInputInfo } from 'mui-tel-input';
import { excludedCountriesList } from '@/constants/phone-numbers';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getConfig from 'next/config';
import { getDiffBetweenDates } from '@/logic/helpers/get-diff-between-dates';
import getLangHeaders from '@/logic/helpers/get-lang-headers';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCity } from '@/hooks';
import { useForm } from 'react-hook-form';
import { useGetCinemas } from '@/logic/services/cinemas';
import { useGetExperiences } from '@/logic/services/experiences';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useRouter } from 'next/router';
import { yupResolver } from '@hookform/resolvers/yup';
import ImageBoxFuture from '@/components/shared/image-box-future';

import ExperienceCard from '@/components/experience/experience-card';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import { EventTypes, PrivateBookingModel } from '@/logic/types/data-layer-models';

// The banner used to be a 1.3 MB JPEG saved as .png, which share previews downloaded
// whole. WebP for the page; a 1200 × 630 JPEG for og:image, the size share cards use.
const privateBookingMainBanner = '/images/private-booking/private-booking.webp';
const privateBookingShareImage = '/images/private-booking/private-booking-og.jpg';

interface Form {
  phoneNumber: string;
  bookingDate: string | null;
  bookingTime: string | null;
  cityId: string;
  experienceId: string;
  cinemaId: string;
  fullName: string;
  email: string;
  film: string;
  numberOfGuests: number;
}

const PrivateBooking: NextPage<{
  path: string;
}> = ({ path }) => {
  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(
    seoDataList?.pageMetaData,
    SeoPagesEnum.privateBooking
  );

  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();
  const setSnack = useSnack(s => s.setSnack, shallow);

  const [countryCode_contact, setCountryCode_contact] = useState('KSA');
  const user = useAuth(s => s.user, shallow);
  const guest = useGuest(s => s.guest, shallow);
  const { cityId } = useCity();
  const [selectedCity, setSelectedCity] = useState(cityId);
  const [selectedExperience, setSelectedExperience] = useState<number>();
  const [selectedCinema, setSelectedCinema] = useState<number>();
  const [recaptchaValue, setRecaptchaValue] = useState<string>('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const cities = useCities(s => s.cities, shallow);

  const [selectedCountry, setSelectedCountry] = useState<country>(null);
  const { publicRuntimeConfig } = getConfig();

  const { data: cinemas } = useGetCinemas({
    params: {
      cityId: selectedCity,
    },
  });

  const { data: experiences } = useGetExperiences({
    params: {
      cityId: selectedCity,
      cinemaId: selectedCinema,
    },
  });

  const schema = yup.object({
    phoneNumber: yup
      .string()
      .test(
        'test-valid-phone',
        intl.formatMessage({ id: 'global.phoneNumber.notValid' }),
        value => {
          if (value) {
            return checkIfPhoneNumberIsValid(value, countryCode_contact);
          }
        }
      ),
    fullName: yup.string().trim().required().min(2).max(100),
    // .matches(/^[aA-zZ\s\u0621-\u064A]+$/),
    email: yup.string().email().required(),
    // .matches(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/),
    cityId: yup.string().required(),
    cinemaId: yup.string().required(),
    experienceId: yup.string().required(),

    bookingDate: yup.string().required(),
    bookingTime: yup.string().required(),
    film: yup.string().trim().required(),
    numberOfGuests: yup.string().required(),
  });

  const {
    register,
    setValue,
    handleSubmit,
    trigger,
    getValues,
    watch,
    reset,
    resetField,
    clearErrors,
    formState: { errors, touchedFields, isValid },
  } = useForm<Form>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      phoneNumber: user?.phoneNumber || undefined,
      bookingDate: '',
      bookingTime: '',
      cityId: `${selectedCity}`,
      experienceId: '',
      cinemaId: '',
      fullName: user?.firstName ? user?.firstName + ' ' + user?.lastName : null,
      email: user?.email || '',
      film: '',
      numberOfGuests: null,
    },
  });

  const createPrivateBooking = useCreatePrivateBooking();

  const triggerDataLayerPrivateBookingEvent = (
    privateBookingData: privateBookingModel
  ): void => {
    const data: PrivateBookingModel = {
      event: EventTypes.PRIVATE_BOOKING,
      cinema_name: cinemas.cinema.find(
        cinema => cinema.id === privateBookingData.cinemaId
      )?.name,
      customer_name: privateBookingData?.fullName,
      email: privateBookingData?.email,
      phone_number: privateBookingData?.phoneNumber,
      location: cities.citiesList.find(city => city.id === privateBookingData.cityId)
        ?.name,
      number_of_guests: privateBookingData?.numberOfGuests,
      experience_name: experiences.experience.find(
        experience => experience.id === privateBookingData.experienceId
      )?.name,
      film_name: privateBookingData?.film,
      selected_time: privateBookingData?.preferdDateTime,
    };
    triggerGAEvent(data, true);
  };

  const onSubmit = handleSubmit(
    async ({
      fullName,
      phoneNumber,
      cityId,
      cinemaId,
      experienceId,
      film,
      numberOfGuests,
      email,
    }) => {
      if (dateTime == '0') {
        setSnack({
          isOpen: true,
          message: 'private_booking.select_time_after_hour_from_now',
          type: 'warning',
        });
      } else {
        const privateBookingData: privateBookingModel = {
          fullName: fullName,
          phoneNumber: user
            ? phoneNumber
            : muviPhoneNumberTrimmer(phoneNumber, selectedCountry.dialCode),
          cityId: +cityId,
          cinemaId: +cinemaId,
          experienceId: +experienceId,
          film: film,
          preferdDateTime: dateTime,
          numberOfGuests: +numberOfGuests,
          email: email,
          recaptchaResponse: recaptchaValue,
        };

        await createPrivateBooking.safeMutateAsync(
          {
            data: privateBookingData,
          },
          {
            onSuccess: () => {
              triggerDataLayerPrivateBookingEvent(privateBookingData);
              setSnack({
                isOpen: true,
                message: 'private_booking.created_successfully',
                type: 'success',
              });

              reset();
              if (user) {
                setValue('phoneNumber', user?.phoneNumber ?? '');
                setValue('email', user?.email ?? '');
                setValue(
                  'fullName',
                  user?.firstName ? user?.firstName + ' ' + user?.lastName : ''
                );
              }
              recaptchaRef.current?.reset();
              close();
              resetField('film');
              resetField('bookingTime');
              resetField('bookingDate');
              resetField('numberOfGuests');
              resetField('experienceId');
              resetField('cinemaId');
              setValue('bookingTime', null);
              setSelectedExperience(null);
              setSelectedCinema(null);
              setSelectedCity(user?.preferredCityId || guest?.preferredCityId || null);
              setRecaptchaValue('');
            },
          }
        );
      }
    }
  );

  const onChangeCaptcha = useCallback(
    value => {
      setRecaptchaValue(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue]
  );

  useEffect(() => {
    setSelectedExperience(null);
    setSelectedCinema(null);
  }, [selectedCity]);

  useEffect(() => {
    setSelectedExperience(null);
  }, [selectedCinema]);

  useEffect(() => {
    clearErrors('fullName');
    clearErrors('email');
    if (user) {
      setValue('phoneNumber', user?.phoneNumber ?? '');
      setValue('email', user?.email ?? '');
      setValue(
        'fullName',
        user?.firstName ? user?.firstName + ' ' + user?.lastName : null
      );
      setValue('cityId', `${user?.preferredCityId}`);
      setSelectedCity(user?.preferredCityId ?? null);
    } else {
      resetField('phoneNumber');
      resetField('fullName');
      resetField('email');
      setValue('phoneNumber', '');
      setValue('email', '');
      setValue('fullName', '');
      setValue('cinemaId', null);
      setValue('numberOfGuests', null);
      setValue('experienceId', null);
      setSelectedExperience(null);
      setSelectedCinema(null);
      resetField('film');
      resetField('bookingDate');
      setValue('bookingTime', null);
      setValue('cityId', `${guest?.preferredCityId}`);
      setSelectedCity(guest?.preferredCityId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const dateTime = useMemo(() => {
    if (
      DateTime.fromISO(getValues().bookingDate).toFormat('dd/MM/yyyy') ===
      DateTime.now().toFormat('dd/MM/yyyy')
    ) {
      const strUnit: _DurationUnits = 'second'.toString() as _DurationUnits;
      if (
        getDiffBetweenDates(
          DateTime.now(),
          DateTime.fromISO(getValues().bookingTime),
          strUnit
        ) >= -3600
      ) {
        return '0';
      }
    }
    const time =
      DateTime.fromISO(getValues().bookingDate).toFormat('dd/MM/yyyy') +
      ' ' +
      DateTime.fromISO(getValues().bookingTime).toFormat('hh:mm a');
    return DateTime.fromFormat(time, 'dd/MM/yyyy hh:mm a').toISO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues().bookingTime, getValues().bookingDate]);

  const minutesOfToday = useMemo(() => {
    return DateTime.now().hour * 60 + DateTime.now().minute;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues().bookingTime, getValues().bookingDate]);

  const dateSelected = {
    '& button': {
      background: getValues()?.bookingDate ? '#F3DEF7' : theme.palette.grey[10],
      color: getValues()?.bookingDate
        ? theme.palette.primary.main
        : theme.palette.grey[80],
      padding: '3px',
      mr: '0px',
    },
  };
  const timeSelected = {
    '& button': {
      background: getValues()?.bookingTime ? '#F3DEF7' : theme.palette.grey[10],
      color: getValues()?.bookingTime
        ? theme.palette.primary.main
        : theme.palette.grey[80],
      padding: '3px',
      mr: '0px',
    },
  };
  const experiencesSelectProps = {
    maxHeight: { tablet: pxToRem(102), mobile: '102px' },
    mb: 0,
    mt: 0,
    border: 'none',
    boxShadow: 'none',

    '& > div': {
      m: 0,
      [theme.breakpoints.down('tablet')]: {
        height: '102px !important',
      },
    },

    '& div div div ': {
      alignItems: { tablet: 'flex-end', mobile: 'flex-start' },
      flexDirection: { tablet: 'row', mobile: 'column' },
    },
    '& p': {
      whiteSpace: 'pre-line',
      WebkitLineClamp: '2',
      width: { tablet: '200px', mobile: '170px' },
    },
    '& img': {
      width: { tablet: pxToRem(92), mobile: '50px' },
      height: { tablet: pxToRem(44), mobile: '30px' },
    },
    '& a,p': {
      fontSize: { tablet: '14px', mobile: '11px' },
    },
  };
  const onKeyDown = e => {
    e.preventDefault();
  };

  return (
    <>
      <Seo
        title={
          seoPage?.titleTag ||
          intl.formatMessage({
            id: 'private_booking',
          })
        }
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={
          seoPage?.descriptionTag ||
          intl.formatMessage({
            id: 'private_booking.description',
          })
        }
        image={privateBookingShareImage}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      <Box
        component='section'
        sx={{
          mb: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: { tablet: pxToRem(30), mobile: '18px' },
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            px: {
              laptop: pxToRem(128),
              tablet: pxToRem(40),
              mobile: '16px',
            },
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='private_booking' />
        </Typography>
        <Box component='div' sx={{ position: 'relative' }}>
          <ImageBoxFuture
            img={privateBookingMainBanner}
            title={intl.formatMessage({
              id: 'private_booking',
            })}
            objectFit='cover'
            sx={{
              width: '100%',
              height: { tablet: pxToRem(1141), mobile: '190px' },
            }}
          />

          <Box
            component='div'
            sx={{
              position: 'absolute',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              top: 0,
              background:
                'linear-gradient(180.8deg, rgba(0, 0, 0, 0.8) 8.82%, rgba(0, 0, 0, 0.3) 34.58%, rgba(0, 0, 0, 0) 53.06%)',
            }}
          >
            <Typography
              component='h2'
              sx={{
                fontSize: { tablet: pxToRem(42), mobile: '16px' },
                color: theme.palette.common.white,
                mt: { tablet: pxToRem(66), mobile: '12px' },
                mb: { tablet: pxToRem(48), mobile: '7px' },
                lineHeight: { tablet: '32px', mobile: '150%' },
                fontWeight: 700,
              }}
            >
              <FormattedMessage id='private_booking.make_your_place' />
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            marginBlockEnd: 5,
            background: theme.palette.layout.bgSecondary,
            maxWidth: '1102px',
            pt: { tablet: pxToRem(50), mobile: '16px' },
            px: { tablet: pxToRem(135), mobile: '16px' },
            pb: { tablet: pxToRem(44), mobile: '25px' },
            mx: 'auto',
            mb: pxToRem(23),
            borderRadius: pxToRem(25),
            [theme.breakpoints.down(1134)]: {
              m: pxToRem(16),
              mt: pxToRem(40),
            },
            mt: { tablet: pxToRem(40), mobile: '24px' },
          }}
        >
          <Typography
            component='h4'
            sx={{
              fontSize: { tablet: pxToRem(28), mobile: '20px' },
              color: theme.palette.primary.main,
              mb: { tablet: pxToRem(41), mobile: '8px' },
              lineHeight: { tablet: '32px', mobile: '30px' },
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            <FormattedMessage id='private_booking' />
          </Typography>
          <Typography
            component='p'
            sx={{
              fontSize: { tablet: pxToRem(24), mobile: '14px' },
              color: theme.palette.grey[70],
              mb: { tablet: pxToRem(24), mobile: '24px' },
              lineHeight: { tablet: '30px', mobile: '24px' },
              fontWeight: 400,
              textAlign: 'center',
            }}
          >
            <FormattedMessage id='private_booking.description' />
          </Typography>
          <Box component='div' sx={{ display: 'flex' }}>
            <RequiredGrayIcon />

            <Typography
              component='p'
              sx={{
                fontSize: '14px',
                color: theme.palette.grey[50],
                mb: { tablet: pxToRem(16), mobile: '20px' },
                lineHeight: '24px',
                fontWeight: 400,
              }}
            >
              <FormattedMessage id='global.all_required' />
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              mt: 3,
              '&.MuiGrid-root>.MuiGrid-item': {
                pt: { tablet: pxToRem(32), mobile: '24px' },
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <TextField
                    id='private-booking-fullName'
                    disabled={Boolean(user?.firstName && user?.lastName)}
                    label={<FormattedMessage id='global.full_name' />}
                    variant='outlined'
                    {...register('fullName')}
                    error={(errors.fullName && touchedFields.fullName) ?? false}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentComponent
                          errorCondition={errors.fullName && touchedFields.fullName}
                        />
                      ),
                    }}
                    sx={{
                      '& fieldset span': {
                        opacity: user?.firstName ? '1!important' : '0',
                        transform: user?.firstName ? 'translateY(-5px)' : 'translateY(0)',
                      },
                      '&  label': {
                        opacity: !user?.firstName ? '1!important' : '0',
                      },
                    }}
                  />
                  <FormHelperText error>
                    {touchedFields.fullName &&
                      (errors?.fullName?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : errors?.fullName?.type === 'min' ||
                        errors?.fullName?.type === 'max' ? (
                        <>
                          <FormattedMessage id='global.please.enter.valid' />
                          &nbsp;
                          <FormattedMessage id='global.full_name' />
                          <FormattedMessage id='global.min.two.max.fifty' />
                        </>
                      ) : (
                        errors?.fullName?.type === 'matches' && (
                          <FormattedMessage id='global.only.chars.allowed' />
                        )
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <TextField
                    id='private-booking-email'
                    {...register('email')}
                    disabled={Boolean(user?.email)}
                    label={<FormattedMessage id='global.email' />}
                    variant='outlined'
                    error={!!(touchedFields.email && errors?.email)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentComponent
                          errorCondition={errors.email && touchedFields.email}
                        />
                      ),
                    }}
                    sx={{
                      '& fieldset span': {
                        opacity: user?.email ? '1!important' : '0',
                        transform: user?.email ? 'translateY(-5px)' : 'translateY(0)',
                      },
                      '&  label': {
                        opacity: !user?.email ? '1!important' : '0',
                      },
                    }}
                  />
                  <FormHelperText error>
                    {touchedFields.email &&
                      (errors?.email?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        (errors?.email?.type === 'email' ||
                          errors?.email?.type === 'matches') && (
                          <FormattedMessage id='global.validation.email' />
                        )
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl variant='standard'>
                  <MuiTelInput
                    excludedCountries={
                      excludedCountriesList.map(c =>
                        c.toUpperCase()
                      ) as MuiTelInputCountry[]
                    }
                    // MuiTelInput is strictly controlled — bind to the watched RHF
                    // value so typing AND programmatic prefill/reset reflect (MUI 7).
                    value={watch('phoneNumber') || ''}
                    error={
                      (errors?.phoneNumber?.type === 'test-valid-phone' &&
                        touchedFields.phoneNumber) ??
                      false
                    }
                    id='phone'
                    InputLabelProps={{
                      sx: {
                        backgroundColor: 'white',
                        paddingInline: '6px',
                      },
                    }}
                    sx={{
                      '& svg': { height: '1em' },
                      legend: {
                        width: 0,
                      },
                    }}
                    defaultCountry={'SA'}
                    dir='ltr'
                    {...register('phoneNumber')}
                    label={<FormattedMessage id='global.phone_number' />}
                    variant={'outlined'}
                    disabled={Boolean(user?.phoneNumber)}
                    onChange={(value: string, info: MuiTelInputInfo) => {
                      setSelectedCountry({
                        countryCode: (info.countryCode ?? '').toLowerCase(),
                        dialCode: info.countryCallingCode ?? '',
                        name: '',
                      });
                      setValue('phoneNumber', value);
                      const field = 'phoneNumber';
                      const c = info.countryCode?.toString().toUpperCase();
                      if (field.includes('contact')) {
                        setCountryCode_contact(c);
                      }
                      trigger(); // only special handle for this mui phoneNumber field
                    }}
                    // mui-tel-input v9 omits InputProps; slotProps.input is spread
                    // after the flag startAdornment, so passing only endAdornment
                    // restores the inline error icon without losing the flag.
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornmentComponent
                            errorCondition={
                              errors.phoneNumber?.type === 'test-valid-phone' &&
                              touchedFields.phoneNumber
                            }
                          />
                        ),
                      },
                    }}
                  />
                  <FormHelperText error>
                    {errors.phoneNumber?.type === 'test-valid-phone' &&
                      touchedFields.phoneNumber && (
                        <FormattedMessage id='global.phoneNumber.notValid' />
                      )}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <InputLabel
                    id='city-label'
                    error={(errors.cityId && touchedFields.cityId) ?? false}
                  >
                    <FormattedMessage id='global.select_city' />
                  </InputLabel>

                  <Select
                    labelId='city-label'
                    id='private-booking-city'
                    label={<FormattedMessage id='global.select_city' />}
                    value={(selectedCity as unknown as string) || getValues().cityId}
                    error={Boolean(errors.cityId)}
                    {...register('cityId')}
                    displayEmpty
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedCity(e.target.value as unknown as number);
                      setValue('cityId', e.target.value as string);
                      trigger();
                    }}
                    MenuProps={{
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'transparent',
                          backdropFilter: 'none',
                        },
                      },
                    }}
                    renderValue={() => {
                      return (
                        <Typography component='span'>
                          {
                            cities?.citiesList?.find(city => city.id === selectedCity)
                              ?.name
                          }
                        </Typography>
                      );
                    }}
                  >
                    {cities?.citiesList?.map(city => (
                      <MenuItem key={city.id} value={city.id}>
                        <Radio checked={selectedCity === city.id} />
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touchedFields.cityId &&
                      (errors?.cityId?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <InputLabel
                    id='cinema-label'
                    error={(errors.cinemaId && touchedFields.cinemaId) ?? false}
                    sx={{
                      color:
                        Boolean(selectedCity) || Boolean(getValues().cityId)
                          ? theme.palette.grey[60]
                          : theme.palette.grey[30],
                    }}
                  >
                    <FormattedMessage id='global.select_cinema' />
                  </InputLabel>

                  <Select
                    labelId='cinema-label'
                    id='private-booking-cinema'
                    label={<FormattedMessage id='global.select_cinema' />}
                    value={getValues().cinemaId || ''}
                    disabled={!Boolean(selectedCity) && !Boolean(getValues().cityId)}
                    {...register('cinemaId')}
                    error={Boolean(errors.cinemaId) && touchedFields.cinemaId}
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedCinema(e.target.value as unknown as number);
                      setValue('cinemaId', e.target.value as string);
                      trigger();
                    }}
                    MenuProps={{
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'transparent',
                          backdropFilter: 'none',
                        },
                      },
                    }}
                    displayEmpty
                    renderValue={() => {
                      return (
                        <Typography component='span'>
                          {
                            cinemas?.cinema?.find(cinema => cinema.id === selectedCinema)
                              ?.name
                          }
                        </Typography>
                      );
                    }}
                  >
                    {cinemas?.cinema?.map(cinema => (
                      <MenuItem key={cinema.id} value={cinema.id}>
                        <Radio checked={selectedCinema === cinema.id} />
                        {cinema.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touchedFields.cinemaId &&
                      (errors?.cinemaId?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <InputLabel
                    id='experience-label'
                    error={(errors.experienceId && touchedFields.experienceId) ?? false}
                    sx={{
                      color: Boolean(selectedCinema)
                        ? theme.palette.grey[60]
                        : theme.palette.grey[30],
                    }}
                  >
                    <FormattedMessage id='private_booking.select_experience' />
                  </InputLabel>

                  <Select
                    labelId='experience-label'
                    id='private-booking-experience'
                    label={<FormattedMessage id='private_booking.select_experience' />}
                    value={getValues().experienceId}
                    disabled={!Boolean(selectedCinema)}
                    {...register('experienceId')}
                    error={Boolean(errors.experienceId) && touchedFields.experienceId}
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedExperience(e.target.value as unknown as number);
                      setValue('experienceId', e.target.value as string);
                      trigger();
                    }}
                    MenuProps={{
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'transparent',
                          backdropFilter: 'none',
                        },
                      },
                    }}
                    displayEmpty
                    renderValue={() => {
                      return (
                        <Typography component='span'>
                          {
                            experiences?.experience?.find(
                              experience => experience.id === selectedExperience
                            )?.name
                          }
                        </Typography>
                      );
                    }}
                  >
                    {experiences?.experience?.map(experience => (
                      <MenuItem
                        key={experience.id}
                        value={experience.id}
                        sx={{
                          mb: { tablet: pxToRem(24), mobile: '0px' },

                          '&  > div': {
                            background:
                              selectedExperience === experience.id
                                ? 'rgb(246 239 248)'
                                : theme.palette.common.white,
                          },
                          ':hover': {
                            '&  > div': {
                              background:
                                selectedExperience === experience.id
                                  ? '#eeddef'
                                  : '#e4e8eb',
                            },
                          },
                        }}
                      >
                        <Radio
                          checked={selectedExperience === experience.id}
                          sx={{ p: { tablet: '9px', mobile: '2px' } }}
                        />

                        <ExperienceCard
                          movieId={0}
                          experience={experience}
                          index={0}
                          sxProps={experiencesSelectProps}
                          hiddenShowTimes
                          experienceLogoSx={{
                            minWidth: 'auto',
                            width: '96px',
                          }}
                          dropDownItem
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touchedFields.experienceId &&
                      (errors?.experienceId?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl sx={dateSelected}>
                  <DatePicker
                    label={<FormattedMessage id='private_booking.booking_date' />}
                    value={
                      getValues()?.bookingDate
                        ? DateTime.fromISO(getValues()?.bookingDate)
                        : null
                    }
                    format='dd/MM/yyyy'
                    {...register('bookingDate')}
                    slotProps={{
                      textField: {
                        onKeyDown: onKeyDown,
                        id: 'private-booking-booking-date',
                        autoComplete: 'off',
                        error:
                          (Boolean(touchedFields.bookingDate) &&
                            Boolean(errors?.bookingDate)) ??
                          false,
                      },
                    }}
                    onChange={e => {
                      if (e != null) {
                        setValue('bookingDate', e.toISO());
                        trigger();
                      }
                      if (
                        DateTime.fromISO(getValues().bookingDate).toFormat(
                          'dd/MM/yyyy'
                        ) === DateTime.now().toFormat('dd/MM/yyyy')
                      ) {
                        setValue('bookingTime', null);
                      }
                    }}
                    minDate={
                      DateTime.now().toFormat('hh a') === '11 PM'
                        ? DateTime.now().plus({ day: 1 })
                        : DateTime.now()
                    }
                  />
                  <FormHelperText error>
                    {touchedFields.bookingDate &&
                      (errors?.bookingDate?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl sx={timeSelected}>
                  <TimePicker
                    ampmInClock={true}
                    // MUI X v6+ defaults desktop time pickers to the digital
                    // clock; restore the v5 analog clock face shown on prod.
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    {...register('bookingTime')}
                    label={<FormattedMessage id='private_booking.booking_time' />}
                    value={
                      getValues()?.bookingTime
                        ? DateTime.fromISO(getValues()?.bookingTime)
                        : null
                    }
                    format='hh:mm a'
                    slotProps={{
                      textField: {
                        id: 'private-booking-booking-time',
                        onKeyDown: onKeyDown,
                        autoComplete: 'off',
                        error: touchedFields.bookingTime && Boolean(errors?.bookingTime),
                      },
                    }}
                    onChange={e => {
                      if (e != null) {
                        if (
                          DateTime.fromISO(getValues().bookingDate).toFormat(
                            'dd/MM/yyyy'
                          ) === DateTime.now().toFormat('dd/MM/yyyy')
                        ) {
                          if (
                            e.hour * 60 + e.minute >=
                            DateTime.now().hour * 60 + DateTime.now().minute + 60
                          ) {
                            setValue('bookingTime', e.toISO());
                            trigger();
                          }
                        } else {
                          setValue('bookingTime', e.toISO());
                          trigger();
                        }
                      }
                    }}
                    minTime={
                      DateTime.fromISO(getValues().bookingDate).toFormat('dd/MM/yyyy') ===
                      DateTime.now().toFormat('dd/MM/yyyy')
                        ? DateTime.now().plus({ minutes: 60 })
                        : DateTime.now().minus({ minutes: minutesOfToday })
                    }
                  />
                  <FormHelperText error>
                    {touchedFields.bookingTime &&
                      (errors?.bookingTime?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <TextField
                    id='private-booking-preferred-movie'
                    {...register('film')}
                    label={
                      <>
                        <FormattedMessage id='private_booking.preferred_movie' />
                      </>
                    }
                    variant='outlined'
                    error={(errors.film && touchedFields.film) ?? false}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentComponent
                          errorCondition={errors.film && touchedFields.film}
                        />
                      ),
                    }}
                  />
                  <FormHelperText error>
                    {touchedFields.film &&
                      (errors?.film?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : errors?.film?.type === 'min' || errors?.film?.type === 'max' ? (
                        <>
                          <FormattedMessage id='global.please.enter.valid' />
                          &nbsp;
                          <FormattedMessage id='global.first_name' />
                          <FormattedMessage id='global.min.two.max.fifty' />
                        </>
                      ) : (
                        errors?.film?.type === 'matches' && (
                          <FormattedMessage id='global.only.chars.allowed' />
                        )
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <TextField
                    id='private-booking-number-of-guests'
                    type='number'
                    {...register('numberOfGuests')}
                    label={
                      <>
                        <FormattedMessage id='private_booking.number_of_guests' />
                      </>
                    }
                    variant='outlined'
                    error={
                      (errors.numberOfGuests && touchedFields.numberOfGuests) ?? false
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentComponent
                          errorCondition={
                            errors.numberOfGuests && touchedFields.numberOfGuests
                          }
                        />
                      ),
                    }}
                  />
                  <FormHelperText error>
                    {touchedFields.numberOfGuests &&
                      (errors?.numberOfGuests?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {!user?.phoneNumber && (
                <Grid item tablet={12} mobile={12} laptop={12}>
                  <FormControl
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',

                      '&.MuiFormControl-root  div > div > div ': {
                        mx: 'auto',
                      },
                    }}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={publicRuntimeConfig.recaptchaClientKey}
                      onChange={onChangeCaptcha}
                      hl={locale}
                    />
                  </FormControl>
                </Grid>
              )}
              <Grid item tablet={12} mobile={12} laptop={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',

                    '& button': {},
                  }}
                >
                  <Button
                    variant='contained'
                    loading={createPrivateBooking.isLoading}
                    sx={{
                      width: { tablet: pxToRem(178), mobile: '145px' },
                      height: { tablet: pxToRem(56), mobile: '56px' },
                      borderRadius: '16px',
                    }}
                    onClick={() => onSubmit()}
                    disabled={
                      !isValid ||
                      (!Boolean(recaptchaValue) && !Boolean(user?.phoneNumber) && !user)
                    }
                  >
                    <Typography
                      component='p'
                      sx={{
                        fontSize: pxToRem(16),
                        fontWeight: '700',
                        lineHeight: '24px',
                      }}
                    >
                      <FormattedMessage id='global.submit' />
                    </Typography>
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const queryClient = new QueryClient();

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default PrivateBooking;
